import React, {useState, useEffect, useMemo, useCallback} from 'react'
import moment from 'moment'
import {orderBy, uniqBy} from 'lodash'
import useReservationEventsReducer from '../../state/reservation/events/reducer'
import useGetConfigurationsReducer from '../../state/reservation/get-configurations/reducer'
import {getConfigurations} from '../../state/reservation/get-configurations/actions'
import useCalendarSearchReducer from '../../state/entities/get-calendar-search-values/reducer'
import {searchCalendar} from '../../state/entities/get-calendar-search-values/actions'

const ReservationCalendarMainContext = React.createContext()

const ReservationCalendarMainProvider = ({children}) => {
  const [selectedDate, setSelectedDate] = useState(moment())
  const [calendarView, setCalendarView] = useState('gantt')
  const [sliderLength, setSliderLength] = useState(1)
  const [dateWiseData, setDateWiseData] = React.useState([])
  const [navigatedDate, setNavigatedDate] = React.useState(null)
  const [realSearch, setRealSearch] = useState('')
  const [sidebarDateClicked, setSidebarDateClicked] = useState(false)
  const [configurations, configurationsDispatch] = useGetConfigurationsReducer()

  const [searchResult, searchCalendardispatch] = useCalendarSearchReducer()

  // get Calendar search data
  const _searchCalendar = useCallback(
    (group_id, params, viewData) => {
      searchCalendar(searchCalendardispatch, group_id, params, viewData)
    },
    [searchCalendardispatch]
  )

  useEffect(() => {
    getConfigurations(configurationsDispatch)

    // run once
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    let _cache = localStorage.getItem('_calendar_main')
    if (_cache) {
      try {
        _cache = JSON.parse(_cache)
        if (_cache.date) {
          setSelectedDate(moment(_cache.date))
        }
        if (_cache.view) {
          setCalendarView(_cache.view)
        }
        if (_cache.slider) {
          setSliderLength(_cache.slider)
        }
      } catch (err) {
        console.log("_calendar_main can't be loaded", err)
      }
    }
  }, [])

  useEffect(() => {
    localStorage.setItem(
      '_calendar_main',
      JSON.stringify({
        date: selectedDate.valueOf(), // assumes v is instance of moment
        view: calendarView,
        slider: sliderLength,
      })
    )
  }, [selectedDate, calendarView, sliderLength])

  const hasEnabledConfiguration = useMemo(() => {
    return configurations.data.filter(f => f.active).length > 0
  }, [configurations])

  const configurationIds = useMemo(() => {
    return configurations.data.map(f => f.entity_id)
  }, [configurations])

  const isSameDate = (firstDate, secondDate) => moment(firstDate).isSame(moment(secondDate), 'day')

  const handleSidebarData = (events, date) => {
    const filteredData = events?.data.filter(
      data => isSameDate(date, data.start) || moment(date).isBetween(data.start, data.end)
    )
    const dateWiseData = uniqBy(orderBy(filteredData, ['start'], ['asc']), v =>
      [v?.id, v?.title].join()
    )
    setDateWiseData(dateWiseData)
    setNavigatedDate(moment(date))
  }

  const value = {
    realSearch,
    setRealSearch,
    selectedDate,
    configurationIds,
    setSelectedDate,
    setDateWiseData,
    setNavigatedDate,
    handleSidebarData,
    isSameDate,
    dateWiseData,
    navigatedDate,
    calendarView,
    setCalendarView,
    sliderLength,
    setSliderLength,
    hasEnabledConfiguration,
    searchResult,
    searchCalendar: _searchCalendar,
    searchCalendardispatch,
    sidebarDateClicked,
    setSidebarDateClicked,
  }

  return (
    <ReservationCalendarMainContext.Provider value={value}>
      {children}
    </ReservationCalendarMainContext.Provider>
  )
}

const ReservationCalendarMainConsumer = ReservationCalendarMainContext.Consumer

export {
  ReservationCalendarMainProvider,
  ReservationCalendarMainContext,
  ReservationCalendarMainConsumer,
}
